'use client';

import { Alert, GlobalLoading, Pagination } from '@/shared/components';
import Catalogs from '../components/catalogs';
import ProductCard from '../components/product-card';
import { useContext, useEffect, useState } from 'react';
import { useQueryCatalogInfo, useQuerySearchResult } from '@/hooks/search-result.hook';
import { useRouter, useSearchParams } from 'next/navigation';
import languageContext from '@/context/language';
import NotFoundSearchResult from '../components/not-found';
import { Trans } from 'react-i18next';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { scrollToTop, uniqueTitle } from '@/app/utils';
import Link from 'next/link';
import { COUNTRY } from '@/app/utils/constant';
import { usePageTracking, useEventTracking } from '@/shared/tracking/use-tracking';
import { useSearchType, usePlacement } from '@/shared/tracking/use-tracking-helpers';
import { searchFilterTracking } from '@/shared/tracking/utils';
import { buildPdpLink, createGptNumberLink } from '@/app/utils/build-pdp-link';
import { getContactUsLink } from '@/app/utils/get-contact-us-link';
import { CatalogProducts } from '@/types/search-result';

export default function SearchResult() {
  const { t } = useLang(NAMESPACE.common);
  const { lang } = useContext(languageContext);
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [frontPageSize, setFrontPageSize] = useState(15);
  const [frontPageNo, setFrontPageNo] = useState(1);
  const [refinement, setRefinement] = useState([]);
  const searchParams = useSearchParams();
  const router = useRouter();
  const [filter, setFilter] = useState('');
  const query = searchParams.get('query');

  const { data, isSuccess, isLoading } = useQuerySearchResult({
    countryCode: COUNTRY,
    language: lang,
    pageNo: currentPage,
    pageSize,
    // https://jira.amer.thermo.com/browse/CHECC-1452
    query: query?.replace(/\([^\)]*\)$/, ''),
    casFilter: searchParams.get('casFilter') || 'false',
    persona: 'catalog',
    filter,
  });

  const { dbcList = [], decList = [] } = data || {};
  const showWarning = dbcList?.length > 0 || decList?.length > 0;
  const isRedirectPdp = data?.pdpRedirectSku || (data?.count === 1 && !showWarning && !filter);
  const showCatalogs = isRedirectPdp ? [] : data?.refinements;
  const [showCatalogResultDTOs, setShowCatalogResultDTOs] = useState<CatalogProducts[]>([]);
  const showPagination = !isRedirectPdp;
  const searchType = useSearchType();
  const { trackEventEffect } = useEventTracking();
  const placement = usePlacement();
  const { mutate, data: catalogInfo } = useQueryCatalogInfo();

  useEffect(() => {
    if (!data?.pageByFrontend) {
      scrollToTop();
      if (data) {
        setShowCatalogResultDTOs(data?.catalogResultDTOs ?? []);
        mutate(data?.catalogResultDTOs?.map((item) => item.childCatalogNumber));
      }
    }
  }, [data]);

  useEffect(() => {
    if (data?.pageByFrontend) {
      const splitDataByPage = data?.catalogResultDTOs?.slice(
        (frontPageNo - 1) * frontPageSize,
        frontPageNo * frontPageSize
      );
      setShowCatalogResultDTOs(splitDataByPage);
      mutate(splitDataByPage?.map((item) => item.childCatalogNumber));
    }
  }, [frontPageNo, frontPageSize, data]);

  useEffect(() => {
    if (catalogInfo) {
      setShowCatalogResultDTOs((prev) => {
        return prev.map((product) => {
          const catalogInfoByCatalogNumber = catalogInfo?.find(
            (item) => item.catalogNumber === product.childCatalogNumber
          );
          return {
            ...product,
            catalogBrand: catalogInfoByCatalogNumber?.brandName || product?.brand,
            catalogTitle: catalogInfoByCatalogNumber?.catalogTitle || product?.catalogName,
          };
        });
      });
    }
  }, [catalogInfo]);

  useEffect(() => {
    if (isRedirectPdp) {
      let pdpUrl = '';
      if (data?.pdpRedirectSku) {
        pdpUrl = buildPdpLink(data?.pdpRedirectSku, {
          skuFromSearch: encodeURIComponent(query || ''),
          typeFromSearch: data?.pdpRedirectType,
        });
      } else {
        pdpUrl = buildPdpLink(data?.catalogResultDTOs?.[0]?.childCatalogNumber);
      }
      router.replace(pdpUrl);
    }
  }, [data, isRedirectPdp]);

  usePageTracking(
    {
      items: [
        {
          key: 'search',
          payload: {
            searchType: isRedirectPdp && searchType !== 'structure search' ? 'catalog number' : searchType,
            keyword: query,
            totalResults: data?.count,
          },
        },
        {
          key: 'productImpressions',
          payload:
            Array.isArray(data?.catalogResultDTOs) && data?.catalogResultDTOs.length > 0
              ? data?.catalogResultDTOs.map((product) => {
                  return {
                    name: uniqueTitle(product.catalogName),
                    catalogId: product.childCatalogNumber,
                    placement: placement,
                  };
                })
              : [],
        },
      ],
    },
    !!data
  );

  useEffect(() => {
    const showLoading = isRedirectPdp || isLoading;
    if (showLoading) {
      GlobalLoading.show();
    } else {
      GlobalLoading.hide();
    }
  }, [isRedirectPdp, isLoading]);

  trackEventEffect(
    {
      key: 'search',
      action: 'search:filtered',
      payload: {
        searchType: searchType,
        keyword: query,
        filters: searchFilterTracking(filter, data?.refinements.length > 0 ? data?.refinements : refinement),
      },
    },
    filter && data?.refinements ? true : false
  );

  const onPageSizeChange = (size) => {
    setPageInfo({ pageNo: 1, pageSize: size });
  };

  const handlePageClick = (page) => {
    setPageInfo({ pageNo: page, pageSize: pageSize });
  };

  const onFiltersChange = (filter) => {
    setRefinement(data?.refinements);
    setFilter(filter);
    setPageInfo({ pageNo: 1 });
  };

  const setPageInfo = ({ pageNo, pageSize }: { pageNo?: number; pageSize?: number }) => {
    if (data?.pageByFrontend) {
      setFrontPageNo(pageNo ?? frontPageNo);
      setFrontPageSize(pageSize ?? frontPageSize);
      scrollToTop();
    } else {
      setCurrentPage(pageNo ?? currentPage);
      setPageSize(pageSize ?? pageSize);
    }
  };

  if (isSuccess) {
    const isNoData = !data?.count && !showWarning;
    if (data?.redirect && data?.gptNumber) {
      router.push(createGptNumberLink(data?.gptNumber));
      return;
    } else if (isNoData) {
      return <NotFoundSearchResult query={query} />;
    }
  }

  return (
    <div>
      <Alert
        className="my-10"
        type="warning"
        open={decList?.length > 0 && !isRedirectPdp}
        content={
          <Trans
            t={t}
            i18nKey="not.web.orderable.tips"
            components={[<Link target="_blank" href={getContactUsLink(lang)} key="custom_service_link"></Link>]}
            values={{ sku: decList?.join(', ') }}
          />
        }
      />

      <Alert
        className="my-10"
        type="warning"
        open={dbcList?.length > 0 && !isRedirectPdp}
        content={
          <Trans
            t={t}
            i18nKey="discontinued.tips"
            components={[<Link target="_blank" href={getContactUsLink(lang)} key="technical_link"></Link>]}
            values={{ sku: dbcList?.join(', ') }}
          />
        }
      />

      <div className="flex sm:flex-col md:flex-col gap-x-10">
        <Catalogs catalogs={showCatalogs} onFiltersChange={onFiltersChange} />
        <div className="flex flex-col flex-1">
          {showCatalogResultDTOs?.map((product, i) => (
            <ProductCard key={product.rootCatalogNumber + product.childCatalogNumber + i} product={product} index={i} />
          ))}
        </div>
      </div>
      {showPagination && (
        <Pagination
          total={data?.count}
          pageSizeOptions={[15, 30, 60]}
          showSizeChanger
          currentPage={data?.pageByFrontend ? frontPageNo : currentPage}
          pageSize={data?.pageByFrontend ? frontPageSize : pageSize}
          onPageChange={handlePageClick}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
}
